'use client';

import { useCallback, useMemo, useState } from 'react';
import { isServer } from '@frameio/utils';
import { ActiveHostEntitiesContext } from '../../shared/contexts/ActiveHostEntitiesContext';
import { SequenceExportDialog } from '../components/SequenceExportDialog';
import { SequencesMetadataSync } from '../components/SequencesMetadataSync';
import { useGetActivePremiereEntitiesQuery } from '../gql';
import { usePremiereProjectEventsSubscription } from '../gql/subscriptions/generated/PremiereProjectEvents';
import { useSequenceEventsSubscription } from '../gql/subscriptions/generated/SequenceEvents';
import { ImportProgressProvider } from './ImportProgressContext';
import { SequenceExportContextProvider } from './SequenceExportContext';

const PREMIERE_PROJECT_TRANSITION_DELAY_MS = 50;

export const ActivePremiereEntitiesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [sequence, setSequence] = useState<{
    sequenceId: string;
    sequenceName: string;
  } | null>(null);
  const [isProjectTransitioning, setIsProjectTransitioning] = useState(false);

  const { data, refetch } = useGetActivePremiereEntitiesQuery({
    context: {
      resolveWithUXP: true,
    },
    skip: isServer,
  });

  const sequenceId =
    sequence?.sequenceId ?? data?.activeProject?.activeSequence?.id ?? null;
  const sequenceName =
    sequence?.sequenceName ?? data?.activeProject?.activeSequence?.name ?? null;

  const refetchActiveEntities = useCallback(async (): Promise<void> => {
    setSequence(null);
    await refetch();
  }, [refetch]);

  const handleProjectOpened = useCallback(() => {
    setIsProjectTransitioning(true);
    setSequence(null);

    // Wait for Premiere to populate activeSequence, then refetch
    setTimeout(() => {
      refetch().finally(() => {
        setIsProjectTransitioning(false);
      });
    }, PREMIERE_PROJECT_TRANSITION_DELAY_MS);
  }, [refetch]);

  useSequenceEventsSubscription({
    onData: (subscriptionData) => {
      const event = subscriptionData.data?.data?.sequenceEvents;

      if (event?.__typename === 'SequenceActivatedPayload') {
        if (isProjectTransitioning) {
          return;
        }

        setSequence({
          sequenceId: event.sequenceId,
          sequenceName: event.sequenceName,
        });
      }

      const wasSequenceClosed =
        event?.__typename === 'SequenceClosedPayload' &&
        event.sequenceId === sequenceId;

      if (wasSequenceClosed) {
        setSequence(null);
        refetch();
      }
    },
    context: {
      resolveWithUXP: true,
    },
  });

  usePremiereProjectEventsSubscription({
    onData: (subscriptionData) => {
      const event = subscriptionData.data?.data?.premiereProjectEvents;

      if (event?.__typename === 'PremiereProjectOpenedPayload') {
        handleProjectOpened();
        return;
      }

      if (event?.__typename === 'PremiereProjectClosedPayload') {
        setSequence(null);
      }
    },
    context: {
      resolveWithUXP: true,
    },
  });

  const activeHostEntitiesValue = useMemo(
    () => ({
      projectId: data?.activeProject?.id ?? null,
      documentId: sequenceId,
      documentName: sequenceName,
      refetchActiveEntities,
    }),
    [data, sequenceId, sequenceName, refetchActiveEntities]
  );

  return (
    <ActiveHostEntitiesContext.Provider value={activeHostEntitiesValue}>
      <SequencesMetadataSync />
      <SequenceExportContextProvider>
        <SequenceExportDialog />
        <ImportProgressProvider>{children}</ImportProgressProvider>
      </SequenceExportContextProvider>
    </ActiveHostEntitiesContext.Provider>
  );
};
